import { useState, useEffect, useCallback, type InputHTMLAttributes, type ReactNode } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button, buttonVariants } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader } from '../../components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Smartphone, Globe, CheckCircle, XCircle, RefreshCw, Eye, Trash2, Search, ShieldCheck, ScrollText } from 'lucide-react';
import { securityApi, Session, AuditLog, SecurityStats } from '../../api/security';
import { Pagination } from '../../components/shared/Pagination';
import { toast } from 'sonner';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { getErrorMessage } from '@/lib/errors';
import { SECURITY_EVENT_LABELS, SECURITY_SEVERITY_LABELS, labelOf } from '@/lib/labels';
import { EmptyState, ErrorState, FormField, KpiCard, PageHeader, StatusPill, Toolbar, type StatusTone } from '@/components/app';

const ITEMS_PER_PAGE = 20;

/** Тон «таблетки» уровня события. */
const SEVERITY_TONE: Record<string, StatusTone> = {
  INFO: 'neutral',
  WARNING: 'warn',
  CRITICAL: 'neg',
};

/** Тон «таблетки» типа события: успех — pos, атаки/ошибки — neg, аномалии — warn. */
const EVENT_TONE: Record<string, StatusTone> = {
  LOGIN_SUCCESS: 'pos',
  LOGIN_FAILED: 'neg',
  ACCOUNT_SHARING_SUSPECTED: 'neg',
  TOKEN_REUSE: 'neg',
  SUSPICIOUS_IP: 'neg',
  SESSION_REVOKED: 'warn',
  DEVICE_LIMIT_EXCEEDED: 'warn',
  GEO_ANOMALY: 'warn',
  RAPID_LOCATION_CHANGE: 'warn',
  RATE_LIMIT_EXCEEDED: 'warn',
  CONCURRENT_SESSIONS: 'warn',
  TIME_MANIPULATION: 'warn',
  DEVICE_MISMATCH: 'warn',
  SESSION_CREATED: 'info',
};

const toneOf = (map: Record<string, StatusTone>, v: string | null | undefined): StatusTone => (v && map[v]) || 'neutral';

const fmt = (n: number | undefined) => new Intl.NumberFormat('ru-RU').format(n || 0);

function SkeletonRows({ cols, rows = 6 }: { cols: number; rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => (
        <TableRow key={i}>
          {Array.from({ length: cols }, (_, j) => (
            <TableCell key={j}>
              <Skeleton className="h-4 w-full max-w-[140px]" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function DetailItem({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-xs text-text-2">{label}</dt>
      <dd className="m-0 break-words text-sm font-medium text-text">{children}</dd>
    </div>
  );
}

function StatRow({ icon, label, value }: { icon: ReactNode; label: string; value: number | undefined }) {
  return (
    <li className="flex items-center justify-between gap-3 border-t border-border-soft py-2.5 first:border-t-0 first:pt-0">
      <span className="flex items-center gap-2 text-text-2 [&_svg]:size-4">{icon}{label}</span>
      <b className="font-display tabular-nums text-text">{fmt(value)}</b>
    </li>
  );
}

// Тема — через CSS-переменные; пропсы маршрута из App.tsx не используются.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function SecurityPage(_props: Record<string, unknown>) {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState<SecurityStats | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Sessions state
  const [sessionsPage, setSessionsPage] = useState(1);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionsSearch, setSessionsSearch] = useState('');
  const [sessionsActiveFilter, setSessionsActiveFilter] = useState<string>('all');
  const [sessionsPlatformFilter, setSessionsPlatformFilter] = useState<string>('all');
  
  // Audit logs state
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsSearch, setLogsSearch] = useState('');
  const [logsSeverityFilter, setLogsSeverityFilter] = useState<string>('all');
  const [logsEventTypeFilter, setLogsEventTypeFilter] = useState<string>('all');
  
  // Поля поиска: в запрос уходят через 300 мс после ввода; ошибки загрузки — отдельно от «не найдено»
  const [sessionsSearchInput, setSessionsSearchInput] = useState('');
  const [logsSearchInput, setLogsSearchInput] = useState('');
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  const [logsError, setLogsError] = useState<string | null>(null);
  const applySessionsSearch = useDebouncedCallback((v: string) => { setSessionsSearch(v); setSessionsPage(1); });
  const applyLogsSearch = useDebouncedCallback((v: string) => { setLogsSearch(v); setLogsPage(1); });

  // Dialogs
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [sessionToRevoke, setSessionToRevoke] = useState<Session | null>(null);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  const loadStats = useCallback(async () => {
    try {
      const data = await securityApi.getStats();
      setStats(data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Не удалось загрузить статистику'));
    }
  }, []);

  const loadSessions = useCallback(async () => {
    setIsLoading(true);
    setSessionsError(null);
    try {
      const response = await securityApi.getSessions({
        page: sessionsPage,
        limit: ITEMS_PER_PAGE,
        search: sessionsSearch || undefined,
        isActive: sessionsActiveFilter !== 'all' ? sessionsActiveFilter : undefined,
        platform: sessionsPlatformFilter !== 'all' ? sessionsPlatformFilter : undefined,
      });
      setSessions(response.data);
      setSessionsTotal(response.total);
    } catch (error) {
      setSessionsError(getErrorMessage(error, 'Не удалось загрузить сессии'));
    } finally {
      setIsLoading(false);
    }
  }, [sessionsPage, sessionsSearch, sessionsActiveFilter, sessionsPlatformFilter]);

  const loadAuditLogs = useCallback(async () => {
    setIsLoading(true);
    setLogsError(null);
    try {
      const response = await securityApi.getAuditLogs({
        page: logsPage,
        limit: ITEMS_PER_PAGE,
        search: logsSearch || undefined,
        severity: logsSeverityFilter !== 'all' ? logsSeverityFilter : undefined,
        eventType: logsEventTypeFilter !== 'all' ? logsEventTypeFilter : undefined,
      });
      setAuditLogs(response.data);
      setLogsTotal(response.total);
    } catch (error) {
      setLogsError(getErrorMessage(error, 'Не удалось загрузить события безопасности'));
    } finally {
      setIsLoading(false);
    }
  }, [logsPage, logsSearch, logsSeverityFilter, logsEventTypeFilter]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (activeTab === 'sessions') {
      loadSessions();
    }
  }, [activeTab, loadSessions]);

  useEffect(() => {
    if (activeTab === 'audit-logs') {
      loadAuditLogs();
    }
  }, [activeTab, loadAuditLogs]);

  const handleRevokeSession = async () => {
    if (!sessionToRevoke) return;
    
    try {
      await securityApi.revokeSession(sessionToRevoke.id);
      toast.success('Сессия отозвана');
      setSessionToRevoke(null);
      setSelectedSession(null);
      loadSessions();
      loadStats();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Не удалось отозвать сессию'));
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelativeTime = (date: string) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'только что';
    if (diffMins < 60) return `${diffMins} мин назад`;
    if (diffHours < 24) return `${diffHours} ч назад`;
    return `${diffDays} д назад`;
  };

  const sessionsPages = Math.max(1, Math.ceil(sessionsTotal / ITEMS_PER_PAGE));
  const logsPages = Math.max(1, Math.ceil(logsTotal / ITEMS_PER_PAGE));

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Безопасность"
        subtitle="Мониторинг сессий, аудит-логов и событий безопасности"
        actions={
          <Button
            variant="secondary"
            onClick={() => {
              loadStats();
              if (activeTab === 'sessions') loadSessions();
              if (activeTab === 'audit-logs') loadAuditLogs();
            }}
          >
            <RefreshCw />
            Обновить
          </Button>
        }
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Обзор</TabsTrigger>
          <TabsTrigger value="sessions">Сессии</TabsTrigger>
          <TabsTrigger value="audit-logs">Аудит-лог</TabsTrigger>
        </TabsList>

        {/* Обзор */}
        <TabsContent value="overview" className="mt-4 flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard label="Активные сессии" value={fmt(stats?.activeSessions)} loading={!stats} />
            <KpiCard label="Активные пользователи" value={fmt(stats?.uniqueActiveUsers)} loading={!stats} />
            <KpiCard label="Критические (24 ч)" value={fmt(stats?.criticalEvents24h)} loading={!stats} />
            <KpiCard label="Подозрительные (7 дн.)" value={fmt(stats?.suspiciousActivity7d)} loading={!stats} />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle>Активность входов (24 ч)</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="m-0 list-none p-0">
                  <StatRow icon={<CheckCircle className="text-pos" />} label="Успешные входы" value={stats?.successfulLogins24h} />
                  <StatRow icon={<XCircle className="text-neg" />} label="Неудачные входы" value={stats?.failedLogins24h} />
                  <StatRow icon={<Trash2 className="text-warn" />} label="Отозвано сессий" value={stats?.revokedSessions24h} />
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Топ стран</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.topCountries && stats.topCountries.length > 0 ? (
                  <ul className="m-0 list-none p-0">
                    {stats.topCountries.slice(0, 5).map((item, index) => (
                      <StatRow key={item.country || index} icon={<Globe />} label={item.country || 'Неизв.'} value={Number(item.count)} />
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-text-2">Нет данных</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>По платформам</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.topPlatforms && stats.topPlatforms.length > 0 ? (
                  <ul className="m-0 list-none p-0">
                    {stats.topPlatforms.slice(0, 5).map((item, index) => (
                      <StatRow
                        key={item.platform || index}
                        icon={<Smartphone />}
                        label={item.platform ? item.platform.toUpperCase() : 'Неизв.'}
                        value={Number(item.count)}
                      />
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-text-2">Нет данных</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Сессии */}
        <TabsContent value="sessions" className="mt-4">
          <Card className="overflow-hidden">
            <div className="grid grid-cols-1 gap-3 border-b border-border-soft p-[18px] sm:grid-cols-[1fr_180px_180px]">
              <FormField label="Поиск" tone="filter">
                <SearchInput
                  placeholder="Устройство, IP или пользователь"
                  value={sessionsSearchInput}
                  onChange={(v) => {
                    setSessionsSearchInput(v);
                    applySessionsSearch(v);
                  }}
                />
              </FormField>
              <FormField label="Статус" tone="filter">
                {(p) => (
                  <Select value={sessionsActiveFilter} onValueChange={(v) => { setSessionsActiveFilter(v); setSessionsPage(1); }}>
                    <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Все статусы</SelectItem>
                      <SelectItem value="true">Активные</SelectItem>
                      <SelectItem value="false">Неактивные</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </FormField>
              <FormField label="Платформа" tone="filter">
                {(p) => (
                  <Select value={sessionsPlatformFilter} onValueChange={(v) => { setSessionsPlatformFilter(v); setSessionsPage(1); }}>
                    <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Все платформы</SelectItem>
                      <SelectItem value="ios">iOS</SelectItem>
                      <SelectItem value="android">Android</SelectItem>
                      <SelectItem value="web">Web</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </FormField>
            </div>

            <Toolbar title={<>Найдено <span className="tabular-nums">{fmt(sessionsTotal)}</span></>} />

            {sessionsError ? (
              <ErrorState message={sessionsError} onRetry={loadSessions} />
            ) : !isLoading && sessions.length === 0 ? (
              <EmptyState icon={<ShieldCheck />} title="Сессии не найдены" description="Измените поиск или фильтры." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Пользователь</TableHead>
                    <TableHead>Устройство</TableHead>
                    <TableHead>Локация</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead>Активность</TableHead>
                    <TableHead className="text-right">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody aria-busy={isLoading || undefined}>
                  {isLoading ? (
                    <SkeletonRows cols={6} />
                  ) : (
                    sessions.map((session) => (
                      <TableRow key={session.id} selected={selectedSession?.id === session.id}>
                        <TableCell>
                          <p className="font-semibold text-text">{session.user?.username || 'Неизв.'}</p>
                          <p className="text-[13px] text-text-2">
                            {session.user?.email || (session.user?.isAnonymous ? 'Аноним' : '—')}
                          </p>
                        </TableCell>
                        <TableCell>
                          <p className="text-text">{session.deviceName || session.deviceModel || 'Неизв. устройство'}</p>
                          <p className="text-xs text-text-2">
                            {session.platform} {session.osVersion}
                          </p>
                        </TableCell>
                        <TableCell>
                          <p className="text-text">{session.country || 'Неизв.'}</p>
                          <p className="text-xs tabular-nums text-text-2">{session.ipAddress || '—'}</p>
                        </TableCell>
                        <TableCell>
                          <StatusPill dot tone={session.isActive ? 'pos' : 'neutral'}>
                            {session.isActive ? 'Активна' : 'Неактивна'}
                          </StatusPill>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-text-2">{formatRelativeTime(session.lastActiveAt)}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label="Подробнее о сессии"
                              title="Подробнее"
                              onClick={() => setSelectedSession(session)}
                            >
                              <Eye />
                            </Button>
                            {session.isActive && (
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label="Отозвать сессию"
                                title="Отозвать"
                                onClick={() => setSessionToRevoke(session)}
                                className="text-neg hover:bg-neg-soft"
                              >
                                <Trash2 />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}

            {!sessionsError && sessions.length > 0 && (
              <Pagination
                currentPage={sessionsPage}
                totalPages={sessionsPages}
                total={sessionsTotal}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setSessionsPage}
              />
            )}
          </Card>
        </TabsContent>

        {/* Аудит-лог */}
        <TabsContent value="audit-logs" className="mt-4">
          <Card className="overflow-hidden">
            <div className="grid grid-cols-1 gap-3 border-b border-border-soft p-[18px] sm:grid-cols-[1fr_180px_240px]">
              <FormField label="Поиск" tone="filter">
                <SearchInput
                  placeholder="IP или устройство"
                  value={logsSearchInput}
                  onChange={(v) => {
                    setLogsSearchInput(v);
                    applyLogsSearch(v);
                  }}
                />
              </FormField>
              <FormField label="Важность" tone="filter">
                {(p) => (
                  <Select value={logsSeverityFilter} onValueChange={(v) => { setLogsSeverityFilter(v); setLogsPage(1); }}>
                    <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Все уровни</SelectItem>
                      <SelectItem value="INFO">{SECURITY_SEVERITY_LABELS.INFO}</SelectItem>
                      <SelectItem value="WARNING">{SECURITY_SEVERITY_LABELS.WARNING}</SelectItem>
                      <SelectItem value="CRITICAL">{SECURITY_SEVERITY_LABELS.CRITICAL}</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </FormField>
              <FormField label="Тип события" tone="filter">
                {(p) => (
                  <Select value={logsEventTypeFilter} onValueChange={(v) => { setLogsEventTypeFilter(v); setLogsPage(1); }}>
                    <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Все события</SelectItem>
                      {Object.entries(SECURITY_EVENT_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </FormField>
            </div>

            <Toolbar title={<>Найдено <span className="tabular-nums">{fmt(logsTotal)}</span></>} />

            {logsError ? (
              <ErrorState message={logsError} onRetry={loadAuditLogs} />
            ) : !isLoading && auditLogs.length === 0 ? (
              <EmptyState icon={<ScrollText />} title="Записи аудита не найдены" description="Измените поиск или фильтры." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Время</TableHead>
                    <TableHead>Событие</TableHead>
                    <TableHead>Важность</TableHead>
                    <TableHead>Пользователь</TableHead>
                    <TableHead>IP / Локация</TableHead>
                    <TableHead className="text-right">Детали</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody aria-busy={isLoading || undefined}>
                  {isLoading ? (
                    <SkeletonRows cols={6} />
                  ) : (
                    auditLogs.map((log) => (
                      <TableRow key={log.id} selected={selectedLog?.id === log.id}>
                        <TableCell className="whitespace-nowrap">
                          <p className="text-text">{formatRelativeTime(log.createdAt)}</p>
                          <p className="text-xs tabular-nums text-text-2">{formatDate(log.createdAt)}</p>
                        </TableCell>
                        <TableCell>
                          <StatusPill tone={toneOf(EVENT_TONE, log.eventType)}>
                            {labelOf(SECURITY_EVENT_LABELS, log.eventType)}
                          </StatusPill>
                        </TableCell>
                        <TableCell>
                          <StatusPill dot tone={toneOf(SEVERITY_TONE, log.severity)}>
                            {labelOf(SECURITY_SEVERITY_LABELS, log.severity)}
                          </StatusPill>
                        </TableCell>
                        <TableCell className="text-text">{log.user?.username || log.userId?.slice(0, 8) || '—'}</TableCell>
                        <TableCell>
                          <p className="tabular-nums text-text">{log.ipAddress || '—'}</p>
                          <p className="text-xs text-text-2">{log.country || '—'}</p>
                        </TableCell>
                        <TableCell className="text-right">
                          {log.details && Object.keys(log.details).length > 0 ? (
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label="Показать детали"
                              title="Детали"
                              onClick={() => setSelectedLog(log)}
                            >
                              <Eye />
                            </Button>
                          ) : (
                            <span className="text-text-3">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}

            {!logsError && auditLogs.length > 0 && (
              <Pagination
                currentPage={logsPage}
                totalPages={logsPages}
                total={logsTotal}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setLogsPage}
              />
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Детали сессии — правая панель */}
      <Sheet open={!!selectedSession} onOpenChange={(o) => !o && setSelectedSession(null)}>
        <SheetContent>
          <SheetHeader title="Детали сессии" />
          <SheetBody>
            <SheetDescription>Полная информация о сессии</SheetDescription>
            {selectedSession && (
              <Card className="p-[18px]">
                <dl className="m-0 grid grid-cols-2 gap-4">
                  <DetailItem label="Пользователь">{selectedSession.user?.username || 'Неизв.'}</DetailItem>
                  <DetailItem label="Email">{selectedSession.user?.email || '—'}</DetailItem>
                  <DetailItem label="Устройство">{selectedSession.deviceName || selectedSession.deviceModel || 'Неизв.'}</DetailItem>
                  <DetailItem label="Платформа">{selectedSession.platform} {selectedSession.osVersion}</DetailItem>
                  <DetailItem label="IP-адрес"><span className="tabular-nums">{selectedSession.ipAddress || '—'}</span></DetailItem>
                  <DetailItem label="Локация">
                    {selectedSession.city ? `${selectedSession.city}, ` : ''}{selectedSession.country || 'Неизв.'}
                  </DetailItem>
                  <DetailItem label="Версия приложения">{selectedSession.appVersion || '—'}</DetailItem>
                  <DetailItem label="Статус">
                    <StatusPill dot tone={selectedSession.isActive ? 'pos' : 'neutral'}>
                      {selectedSession.isActive ? 'Активна' : 'Неактивна'}
                    </StatusPill>
                  </DetailItem>
                  <DetailItem label="Создана"><span className="tabular-nums">{formatDate(selectedSession.createdAt)}</span></DetailItem>
                  <DetailItem label="Активность"><span className="tabular-nums">{formatDate(selectedSession.lastActiveAt)}</span></DetailItem>
                </dl>
              </Card>
            )}
          </SheetBody>
          {selectedSession?.isActive && (
            <SheetFooter>
              <Button variant="destructiveOutline" onClick={() => setSessionToRevoke(selectedSession)}>
                Отозвать сессию…
              </Button>
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>

      {/* Детали события — правая панель */}
      <Sheet open={!!selectedLog} onOpenChange={(o) => !o && setSelectedLog(null)}>
        <SheetContent>
          <SheetHeader title="Детали события" />
          <SheetBody>
            {selectedLog && (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill tone={toneOf(EVENT_TONE, selectedLog.eventType)}>
                    {labelOf(SECURITY_EVENT_LABELS, selectedLog.eventType)}
                  </StatusPill>
                  <StatusPill dot tone={toneOf(SEVERITY_TONE, selectedLog.severity)}>
                    {labelOf(SECURITY_SEVERITY_LABELS, selectedLog.severity)}
                  </StatusPill>
                </div>
                <SheetDescription>
                  <span className="tabular-nums">{formatDate(selectedLog.createdAt)}</span> ·{' '}
                  {selectedLog.user?.username || selectedLog.userId?.slice(0, 8) || '—'} · {selectedLog.ipAddress || '—'}
                </SheetDescription>
                <pre className="m-0 overflow-auto rounded-xl border border-border-soft bg-surface p-4 font-mono text-xs text-text">
                  {JSON.stringify(selectedLog.details, null, 2)}
                </pre>
              </>
            )}
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Подтверждение отзыва сессии */}
      <AlertDialog open={!!sessionToRevoke} onOpenChange={(o) => !o && setSessionToRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Отозвать сессию?</AlertDialogTitle>
            <AlertDialogDescription>
              Пользователь будет разлогинен на этом устройстве.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevokeSession} className={buttonVariants({ variant: 'destructive' })}>
              Отозвать
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SearchInput({
  value,
  onChange,
  placeholder,
  ...rest
}: { value: string; onChange: (v: string) => void; placeholder: string } & Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'>) {
  return (
    <span className="relative block">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-3" aria-hidden />
      <Input {...rest} type="search" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} className="pl-9" />
    </span>
  );
}
